import React, { memo, useState } from 'react';
import { User, UsersRound } from 'lucide-react';
import { cn } from '../lib/utils';
import { cachedAvatarUrl } from '../lib/contactAvatarCache';

/** Extrai DDD BR (2 dígitos) de telefone ou nome numérico. */
export function extractContactDdd(...sources) {
  for (const src of sources) {
    if (String(src || '').trim().startsWith('+') && !String(src).trim().startsWith('+55')) continue;
    const digits = String(src || '').replace(/\D/g, '');
    if (!digits) continue;
    // 55 + DDD + número (ex.: 5511999999999)
    if (digits.startsWith('55') && digits.length >= 12 && digits.length <= 13) {
      return digits.slice(2, 4);
    }
    // DDD + número sem país (10–11 dígitos)
    if (digits.length === 10 || digits.length === 11) {
      return digits.slice(0, 2);
    }
  }
  return null;
}

function ContactAvatarInner({
  name,
  phone,
  profilePic,
  size = 'md',
  selected = false,
  isGroup = false,
  status,
  waPresenceAt,
  waOnline,
  profileRevision,
  onPhotoError,
  className,
}) {
  const sizeClass =
    size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm';

  const ddd = extractContactDdd(phone, name);
  const [failedPhoto, setFailedPhoto] = useState('');
  const photoUrl = isGroup ? '' : cachedAvatarUrl(profilePic);
  const photoIdentity = photoUrl ? `${photoUrl}:${String(profileRevision || '')}` : '';
  const showPhoto = photoUrl && photoIdentity !== failedPhoto;

  const useWa = waPresenceAt != null && waPresenceAt !== '';
  const statusColor = useWa
    ? waOnline
      ? 'bg-emerald-500'
      : 'bg-zinc-500'
    : status === 'open'
      ? 'bg-emerald-500'
      : status === 'waiting'
        ? 'bg-amber-500'
        : 'bg-zinc-500';

  const shapeClass = 'office-avatar rounded-[3px]';
  const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
  const dddClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-lg' : 'text-sm';

  return (
    <div className={cn('contact-avatar relative flex-shrink-0', sizeClass, className)} role="img" aria-label={isGroup ? 'Grupo' : showPhoto ? `Foto de ${name || 'contato'}` : ddd ? `DDD ${ddd}` : 'Contato'}>
      <div className={cn(sizeClass, 'relative overflow-hidden ring-1 ring-primary/20 bg-primary/10', shapeClass, selected && 'ring-primary/40')}>
        {/* Foto já salva no Office; falha mantém o DDD, sem consultar o WhatsApp. */}
        <div
          className={cn(
            'absolute inset-0 flex flex-col items-center justify-center gap-0.5',
            'text-primary',
            selected ? 'bg-primary/20' : 'bg-primary/10',
          )}
          aria-hidden="true"
        >
          {isGroup ? (
            <UsersRound className={cn(iconSize, 'text-primary')} strokeWidth={1.75} />
          ) : ddd ? (
            <span className={cn('font-semibold tabular-nums leading-none tracking-wide', dddClass)}>{ddd}</span>
          ) : <User className={iconSize} strokeWidth={1.75} />}
        </div>
        {showPhoto ? <img src={photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover"
          loading="lazy" decoding="async" onError={() => {
            setFailedPhoto(photoIdentity);
            onPhotoError?.();
          }} /> : null}
      </div>
      {!isGroup && (status || useWa) ? (
        <div
          className={cn(
            'absolute -bottom-0 -right-0 z-10 h-3 w-3 rounded-full ring-2 ring-card',
            statusColor,
          )}
          aria-hidden
        />
      ) : null}
    </div>
  );
}

const ContactAvatar = memo(ContactAvatarInner);
export default ContactAvatar;
